import React, { useContext, useState, useEffect } from 'react';
import { SettingsContext } from '../../../context/SettingsProvider';
import { UserContext } from '../../../context/UserProvider';
import ResourceModal from '../../../components/modals/ResourceModal';
import DeleteModal from "../../../components/modals/DeleteModal";
import SuccessNotification from "../../../components/toast/SuccessNotification";
import ErrorNotification from "../../../components/toast/ErrorNotification";

const Resources = () => {
  const { resources, getResources, postResourceFile, postResourceLink, deleteResource } = useContext(SettingsContext);
  const { token } = useContext(UserContext);
  const [newResourceName, setNewResourceName] = useState('');
  const [newResourceType, setNewResourceType] = useState('File');
  const [newResourceContent, setNewResourceContent] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [resourceToDelete, setResourceToDelete] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [text, setText] = useState("");

  const closeNotification = () => {
    setShowSuccess(false);
    setShowError(false);
  };

  const openModal = () => {
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
  };

  const handleDeleteResource = (id) => {
    setResourceToDelete(id);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirmation = async () => {
    if (resourceToDelete) {
      try {
        await deleteResource(resourceToDelete);
        setResourceToDelete(null);
        setShowDeleteModal(false);
        setText("Resource deleted successfully.");
        setShowSuccess(true);
      } catch (error) {
        console.error(`Failed to delete resource with ID: ${resourceToDelete}, Error: ${error}`);
        setText("Failed to delete resource.");
        setShowError(true);
      }
    }
  };

  const handleNameChange = (event) => {
    setNewResourceName(event.target.value);
  };

  const handleContentChange = (event) => {
    setNewResourceContent(event.target.value);
  };

  const handleUpload = async () => {
    try {
      const newResource = {
        id: resources.length + 1,
        name: newResourceName,
        type: newResourceType,
        content: newResourceContent,
      };
      if (newResource.type === 'File') {
        const file = document.getElementById('fileUpload').files[0];
        const formData = new FormData();
        formData.append('name', newResource.name);
        formData.append('file', file, file.name);
        await postResourceFile(formData);
        setText("Successfully uploaded file.");
      } else {
        await postResourceLink(newResource);
        setText("Successfully added link.");
      }
      setShowSuccess(true);
      closeModal();
      setNewResourceName('');
      setNewResourceType('File');
      setNewResourceContent('');
    } catch (error) {
      console.error('Upload failed:', error);
      setText("Upload failed.");
      setShowError(true);
    }
  };

  const handleClick = async (resource) => {
    if (resource.file === false) {
      let url = resource.link;
      if (!/^https?:\/\//i.test(url)) {
        url = 'http://' + url;
      }
      window.open(url, '_blank');
    } else {
      try {
        const response = await fetch(resource.link, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        const contentType = response.headers.get('Content-Type');
        const blob = await response.blob();
        const downloadUrl = URL.createObjectURL(new Blob([blob], { type: contentType }));
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = resource.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error('There was a problem with the fetch operation:', error.message);
      }
    }
  };

  useEffect(() => {
    getResources();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <div id="resources" className="mt-3 mx-10 mb-3 w-3/4 max-w-4xl shadow overflow-hidden sm:rounded-lg">
        <div className="p-2 flex justify-between bg-gray-400 dark:bg-stone-700">
          <button
            onClick={openModal}
            className="bg-blue-600 hover:bg-blue-500 dark:bg-indigo-500 dark:hover:bg-indigo-600 text-white font-semibold px-2 py-1 rounded"
          >
            Add Resource
          </button>
        </div>
        <div className="max-h-[80vh] overflow-y-scroll">
          <table className="min-w-full">
            <thead className="bg-gray-500 dark:bg-stone-500 sticky top-0 z-9">
            <tr>
              <th className="px-6 py-3 w-full text-left text-xs font-medium text-gray-100 uppercase tracking-wider select-none">
                Label
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-100 uppercase tracking-wider select-none">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-100 uppercase tracking-wider select-none">
                {/*Actions*/}
              </th>
            </tr>
            </thead>
            <tbody className="bg-white dark:bg-stone-900 divide-y divide-gray-200 dark:divide-gray-600">
            {resources.map((resource, index) => (
              <tr key={index}>
                <td className="px-6 py-3 whitespace-nowrap">
                  <a
                    href={resource.content}
                    className="text-sm text-gray-900 dark:text-gray-300 select-none"
                  >
                    {resource.name}
                  </a>
                </td>
                <td className="px-6 py-3 whitespace-nowrap">
                  <div className="text-sm text-gray-900 dark:text-gray-300">
                    {resource.file === true ? 'File' : 'Link'}
                  </div>
                </td>
                <td className="px-6 py-3 whitespace-nowrap text-right text-sm font-medium">
                  <button
                    className="text-blue-600 dark:text-indigo-500 hover:text-blue-800 dark:hover:text-blue-400 mr-4 select-none text-center"
                    onClick={() => handleClick(resource)}
                  >
                    {resource.file === true ? 'Download' : 'Visit Link'}
                  </button>
                  <button
                    onClick={() => handleDeleteResource(resource.id)}
                    className="text-red-400 hover:text-red-500 font-semibold px-2 py-1 rounded"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-6 h-6"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                      />
                    </svg>
                  </button>
                </td>
              </tr>
            ))}
            </tbody>
          </table>
        </div>
        {isOpen && (
          <ResourceModal
            isOpen={isOpen}
            onClose={closeModal}
            onSave={handleUpload}
            title={'Add Resource'}
          >
            <div className="flex flex-col w-64 ml-2">
              <input
                type="text"
                placeholder="Enter resource name"
                value={newResourceName}
                maxLength="125"
                onChange={handleNameChange}
                className="w-full px-3 py-2 my-4 border text-gray-700 bg-gray-100 border-gray-300 rounded-md dark:bg-stone-700 dark:border-stone-800 dark:text-gray-300"
              />
              <select
                value={newResourceType}
                onChange={(e) => setNewResourceType(e.target.value)}
                className="w-full px-3 py-2 mb-4 border text-gray-600 bg-gray-100 border-gray-300 rounded-md dark:bg-stone-700 dark:border-stone-800 dark:text-gray-300"
              >
                <option value="File">File</option>
                <option value="URL">URL</option>
              </select>
              {newResourceType === 'File' ? (
                <>
                  <input
                    type="file"
                    id="fileUpload"
                    className="hidden"
                    onChange={(e) => setNewResourceContent(URL.createObjectURL(e.target.files[0]))}
                  />
                  <label
                    htmlFor="fileUpload"
                    className="bg-gray-500 dark:hover:bg-gray-600 text-white font-semibold py-2 px-4 rounded inline-flex items-center cursor-pointer"
                  >
                    Browse
                  </label>
                </>
              ) : (
                <input
                  type="text"
                  placeholder="Enter URL"
                  value={newResourceContent}
                  maxLength="512"
                  onChange={handleContentChange}
                  className="w-full h-10 px-3 py-2 border text-gray-700 bg-gray-100 border-gray-300 rounded-md dark:bg-stone-700 dark:border-stone-800 dark:text-gray-300"
                />
              )}
            </div>
          </ResourceModal>
        )}
        {showDeleteModal && (
          <DeleteModal
            title="Are you sure?"
            onCancel={() => {
              setShowDeleteModal(false);
              setResourceToDelete(null);
            }}
            onDelete={handleDeleteConfirmation}
          />
        )}
      </div>
      {showSuccess && (
        <SuccessNotification show={showSuccess} onClose={closeNotification} text={text} />
      )}
      {showError && (
        <ErrorNotification show={showError} onClose={closeNotification} text={text} />
      )}
    </div>
  );
};

export default Resources;
